import React, { useState, useRef, useEffect } from 'react';
import { Check, ChevronDown, X, Search } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Input } from './input';
import { Badge } from './badge';

const STATIONERY_ITEMS = [
  "Pen",
  "Fevistick",
  "Fevicol",
  "Highlighter",
  "Scissors",
  "Cutter",
  "Stamp Pad",
  "Stamp Ink Bottle",
  "Punching Machine",
  "Stapler",
  "Stapler Pins",
  "Calculator (Casio)",
  "Pencil",
  "Sharpener",
  "Eraser",
  "Divider",
  "Writing Pad",
  "Matrix Note Book",
  "Rubber Band",
  "Binder Clip",
  "Post It",
  "CD Marker",
  "Sketch Pen",
  "Zip Bag",
  "UPIN",
  "Green Cloth",
  "Envelopes",
  "PPL A4 Size – Neo",
  "PPL FC Size – Neo",
  "Punch Folder FC",
  "Punch Folder A4",
  "Lamina Morocco",
  "Report File",
  "Box File",
  "Flat Files",
  "Voucher File",
  "Spring File Kobra",
  "A4 Size Paper JK",
  "A3 Size Paper JK",
  "FC Size Paper Green",
  "FC Size Paper White",
  "Cello Tape",
  "Board Pin",
  "Board Duster",
  "White Board Marker Black",
  "White Board Marker Blue",
  "Whitener Pen",
  "Duracell Small AAA",
  "Duracell AA",
  "Confidential Sticker"
];

export function StationeryMultiSelect({ value = [], onChange, className }) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const containerRef = useRef(null);
  const searchInputRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const filteredItems = STATIONERY_ITEMS.filter(item =>
    item.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const selectedItems = value || [];

  const toggleItem = (item) => {
    const existingItem = selectedItems.find(i => i.name === item);
    if (existingItem) {
      onChange(selectedItems.filter(i => i.name !== item));
    } else {
      onChange([...selectedItems, { name: item, quantity: 1 }]);
    }
  };

  const updateQuantity = (itemName, quantity) => {
    const qty = Math.max(1, parseInt(quantity) || 1);
    onChange(selectedItems.map(item => 
      item.name === itemName ? { ...item, quantity: qty } : item
    ));
  };

  const removeItem = (itemName, e) => {
    e.stopPropagation();
    onChange(selectedItems.filter(i => i.name !== itemName));
  };

  const isSelected = (item) => selectedItems.some(i => i.name === item);

  return (
    <div ref={containerRef} className={cn("relative", className)}>
      {/* Selected Items Display */}
      <div
        onClick={() => setIsOpen(!isOpen)}
        className="min-h-[42px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm cursor-pointer flex flex-wrap gap-1.5 items-center"
        data-testid="stationery-multiselect"
      >
        {selectedItems.length === 0 ? (
          <span className="text-muted-foreground">Select stationery items...</span>
        ) : (
          selectedItems.map((item) => (
            <Badge
              key={item.name}
              variant="secondary"
              className="flex items-center gap-1 pr-1"
            >
              <span>{item.name}</span>
              <span className="text-xs text-muted-foreground">x{item.quantity}</span>
              <button
                onClick={(e) => removeItem(item.name, e)}
                className="ml-1 hover:bg-muted rounded-full p-0.5"
              >
                <X className="w-3 h-3" />
              </button>
            </Badge>
          ))
        )}
        <ChevronDown className={cn(
          "ml-auto w-4 h-4 text-muted-foreground transition-transform",
          isOpen && "rotate-180"
        )} />
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 mt-1 w-full rounded-md border bg-popover shadow-lg">
          {/* Search Box */}
          <div className="p-2 border-b">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                ref={searchInputRef}
                type="text"
                placeholder="Search items..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8 h-9"
                data-testid="stationery-search"
              />
            </div>
          </div>

          {/* Items List */}
          <div className="max-h-[280px] overflow-y-auto p-1">
            {filteredItems.length === 0 ? (
              <div className="py-4 text-center text-sm text-muted-foreground">
                No items found
              </div>
            ) : (
              filteredItems.map((item) => {
                const selected = isSelected(item);
                const selectedItem = selectedItems.find(i => i.name === item);
                
                return (
                  <div
                    key={item}
                    className={cn(
                      "flex items-center gap-2 px-2 py-1.5 rounded-sm cursor-pointer text-sm",
                      selected ? "bg-accent" : "hover:bg-muted"
                    )}
                  >
                    <div
                      onClick={() => toggleItem(item)}
                      className="flex items-center gap-2 flex-1"
                    >
                      <div className={cn(
                        "w-4 h-4 border rounded flex items-center justify-center",
                        selected ? "bg-primary border-primary" : "border-input"
                      )}>
                        {selected && <Check className="w-3 h-3 text-primary-foreground" />}
                      </div>
                      <span>{item}</span>
                    </div>
                    
                    {selected && (
                      <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                        <span className="text-xs text-muted-foreground">Qty:</span>
                        <Input
                          type="number"
                          min="1"
                          value={selectedItem?.quantity || 1}
                          onChange={(e) => updateQuantity(item, e.target.value)}
                          className="w-16 h-7 text-xs"
                          data-testid={`qty-${item.replace(/\s+/g, '-').toLowerCase()}`}
                        />
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>

          {/* Footer with count */}
          <div className="p-2 border-t text-xs text-muted-foreground">
            {selectedItems.length} item{selectedItems.length !== 1 ? 's' : ''} selected
          </div>
        </div>
      )}
    </div>
  );
}

export { STATIONERY_ITEMS };
